// src/services/gridLoadLetterService.ts

import { StaffAllocationGrid, ClassAllocation, LineDefinition } from '../types/grid';
import { GridsService } from './gridsService';
import { Semester } from '../types/loadLetter';
import { resolveClassMinutes } from '../utils/classMinutes';

/**
 * Grid Load Letter Integration Service
 *
 * Connects the Grid system with the Load Letter system
 */

const gridsService = new GridsService();

/**
 * Get the currently active grid
 */
export const getActiveGrid = async (): Promise<StaffAllocationGrid | null> => {
  try {
    const grids = await gridsService.getAllGrids();

    // Find the first active grid, or return the most recent one
    const activeGrid = grids.find(g => g.isActive !== false);

    return activeGrid || (grids.length > 0 ? grids[0] : null);
  } catch (error) {
    console.error('Error getting active grid:', error);
    throw error;
  }
};

/**
 * Get all available grids for selection
 */
export const getAllAvailableGrids = async (): Promise<StaffAllocationGrid[]> => {
  try {
    return await gridsService.getAllGrids();
  } catch (error) {
    console.error('Error getting available grids:', error);
    throw error;
  }
};

/**
 * Extract staff codes from a line assignment value
 * Handles both simple strings and JSON arrays with teacher objects
 */
const extractStaffCodesFromAssignment = (assignmentValue: string | null): string[] => {
  if (!assignmentValue) {
    return [];
  }

  // Check if it's a JSON array (starts with '[')
  if (assignmentValue.trim().startsWith('[')) {
    try {
      const parsed = JSON.parse(assignmentValue);
      if (Array.isArray(parsed)) {
        return parsed
          .filter(item => item && item.teacherCode)
          .map(item => item.teacherCode);
      }
    } catch (e) {
      console.warn('Failed to parse line assignment JSON:', assignmentValue, e);
      return [];
    }
  }

  // Simple staff code string
  return [assignmentValue];
};

/**
 * Check if a staff code matches an assignment value
 * Handles both simple strings and JSON arrays
 */
const staffCodeMatchesAssignment = (staffCode: string, assignmentValue: string | null): boolean => {
  if (!assignmentValue || !staffCode) {
    return false;
  }

  const codes = extractStaffCodesFromAssignment(assignmentValue);
  return codes.some(code =>
    code.toUpperCase() === staffCode.toUpperCase()
  );
};

/**
 * Get classes assigned to a specific staff member for a given semester
 * NOTE: This function now expects the NEW staffCode field (e.g., 'AC', 'AJE')
 * Grid assignments may still use the old 'initial' codes, so we match case-insensitively
 */
export const getStaffClasses = (
  grid: StaffAllocationGrid,
  staffCode: string,
  semester: Semester
): ClassAllocation[] => {
  if (!grid || !grid.classes) {
    return [];
  }

  // Get lines for the specified semester
  const semesterLines = getLinesForSemester(grid, semester);
  const semesterLineIds = semesterLines.map(line => line.id);

  // Debug: Only log for first few staff codes (using new codes like AT, DK, AD)
  const shouldDebug = ['AT', 'DK', 'AD'].includes(staffCode);
  if (shouldDebug) {
    console.log(`[getStaffClasses] Looking for staff ${staffCode} in semester ${semester}`);
    console.log(`[getStaffClasses] Semester line IDs:`, semesterLineIds);
  }

  const staffClasses: ClassAllocation[] = [];
  let checkedClasses = 0;
  let matchedLineIds = new Set<string>();

  // Check each class in the grid
  grid.classes.forEach((classItem) => {
    if (classItem.lineAssignments) {
      Object.entries(classItem.lineAssignments).forEach(([lineId, assignmentValue]) => {
        checkedClasses++;

        // Debug first match attempt
        if (shouldDebug && checkedClasses <= 3) {
          console.log(`[getStaffClasses] Checking class ${classItem.className}:`, {
            lineId,
            assignmentValue,
            inSemesterLines: semesterLineIds.includes(lineId),
            matches: staffCodeMatchesAssignment(staffCode, assignmentValue)
          });
        }

        // Check if this line is for the correct semester and assigned to this staff member
        if (
          semesterLineIds.includes(lineId) &&
          staffCodeMatchesAssignment(staffCode, assignmentValue)
        ) {
          staffClasses.push(classItem);
          matchedLineIds.add(lineId);
        }
      });
    }
  });

  if (shouldDebug) {
    console.log(`[getStaffClasses] Staff ${staffCode}: found ${staffClasses.length} classes on lines:`, Array.from(matchedLineIds));
  }

  return staffClasses;
};

/**
 * Get lines for a specific semester
 */
export const getLinesForSemester = (
  grid: StaffAllocationGrid,
  semester: Semester
): LineDefinition[] => {
  if (!grid || !grid.lines) {
    console.warn('[getLinesForSemester] No grid or lines:', { hasGrid: !!grid, hasLines: !!grid?.lines });
    return [];
  }

  const lines: LineDefinition[] = [];

  console.log(`[getLinesForSemester] Checking ${grid.lines.length} lines for semester: ${semester}`);

  // Log first few lines to see their structure
  grid.lines.slice(0, 3).forEach(line => {
    console.log(`[getLinesForSemester] Sample line:`, {
      id: line.id,
      name: line.name,
      studyPeriod: line.studyPeriod,
      studyPeriodLower: line.studyPeriod?.toLowerCase()
    });
  });

  grid.lines.forEach((line) => {
    const studyPeriod = line.studyPeriod?.toLowerCase();
    const lineId = (line.id || '').toString().toUpperCase();

    // If showing full year, include all lines
    if (semester === 'both') {
      lines.push(line);
      return;
    }

    // If studyPeriod is explicitly set, use it
    if (studyPeriod) {
      if (semester === 'semester1') {
        if (studyPeriod === 'semester1' || studyPeriod === 'fullyear') {
          lines.push(line);
        }
      } else if (semester === 'semester2') {
        if (studyPeriod === 'semester2' || studyPeriod === 'fullyear') {
          lines.push(line);
        }
      }
      return;
    }

    // FALLBACK: If studyPeriod is undefined, use line ID convention
    // Traditional line mappings: A-F = Semester 1, G-L = Semester 2
    // This matches the logic in StaffAllocationGrid.tsx:678-686
    if (semester === 'semester1') {
      if (['A', 'B', 'C', 'D', 'E', 'F'].includes(lineId)) {
        lines.push(line);
      }
    } else if (semester === 'semester2') {
      if (['G', 'H', 'I', 'J', 'K', 'L'].includes(lineId)) {
        lines.push(line);
      }
    }
  });

  console.log(`[getLinesForSemester] Found ${lines.length} lines for ${semester}, line IDs:`, lines.map(l => l.id));

  return lines;
};

/**
 * Calculate teaching minutes for a staff member from grid data
 */
export const calculateTeachingMinutesFromGrid = (
  grid: StaffAllocationGrid,
  staffCode: string,
  semester: Semester
): number => {
  const classes = getStaffClasses(grid, staffCode, semester);

  let totalMinutes = 0;

  classes.forEach((classItem) => {
    // Use class-specific minutes if available, otherwise use grid default
    const classMinutes = resolveClassMinutes(classItem, grid);
    totalMinutes += classMinutes;
  });

  return totalMinutes;
};

/**
 * Get all staff members who have classes in the grid
 */
export const getStaffCodesFromGrid = (grid: StaffAllocationGrid): string[] => {
  if (!grid || !grid.classes) {
    return [];
  }

  const staffCodes = new Set<string>();

  grid.classes.forEach((classItem) => {
    if (classItem.lineAssignments) {
      Object.values(classItem.lineAssignments).forEach((assignmentValue) => {
        const codes = extractStaffCodesFromAssignment(assignmentValue);
        codes.forEach(code => staffCodes.add(code));
      });
    }
  });

  return Array.from(staffCodes);
};

/**
 * Extract load data for all staff in the grid
 */
export const extractAllStaffLoads = (
  grid: StaffAllocationGrid,
  semester: Semester
): Map<string, { classes: ClassAllocation[]; teachingMinutes: number }> => {
  const staffLoads = new Map<string, { classes: ClassAllocation[]; teachingMinutes: number }>();

  const staffCodes = getStaffCodesFromGrid(grid);

  console.log('[extractAllStaffLoads] Grid staff codes found:', staffCodes);
  console.log('[extractAllStaffLoads] Semester:', semester);
  console.log('[extractAllStaffLoads] Total classes in grid:', grid.classes?.length);
  console.log('[extractAllStaffLoads] Total lines in grid:', grid.lines?.length);

  // Sample some classes to see their line assignments
  if (grid.classes && grid.classes.length > 0) {
    console.log('[extractAllStaffLoads] Sample class:', {
      className: grid.classes[0].className,
      lineAssignments: grid.classes[0].lineAssignments
    });
  }

  staffCodes.forEach((staffCode) => {
    const classes = getStaffClasses(grid, staffCode, semester);
    const teachingMinutes = calculateTeachingMinutesFromGrid(grid, staffCode, semester);

    console.log(`[extractAllStaffLoads] Staff ${staffCode}: ${classes.length} classes, ${teachingMinutes} minutes`);

    staffLoads.set(staffCode, {
      classes,
      teachingMinutes,
    });
  });

  return staffLoads;
};

/**
 * Extract load data for all staff across multiple grids (combined)
 */
export const extractAllStaffLoadsFromMultipleGrids = (
  grids: StaffAllocationGrid[],
  semester: Semester
): Map<string, { classes: ClassAllocation[]; teachingMinutes: number }> => {
  const combinedLoads = new Map<string, { classes: ClassAllocation[]; teachingMinutes: number }>();

  console.log(`[extractAllStaffLoadsFromMultipleGrids] Combining ${grids.length} grids for semester ${semester}`);

  grids.forEach((grid) => {
    console.log(`[extractAllStaffLoadsFromMultipleGrids] Processing grid: ${grid.name}`);

    const gridLoads = extractAllStaffLoads(grid, semester);

    gridLoads.forEach((load, staffCode) => {
      if (combinedLoads.has(staffCode)) {
        // Combine with existing data
        const existing = combinedLoads.get(staffCode)!;
        combinedLoads.set(staffCode, {
          classes: [...existing.classes, ...load.classes],
          teachingMinutes: existing.teachingMinutes + load.teachingMinutes,
        });
      } else {
        // Add new staff member
        combinedLoads.set(staffCode, {
          classes: [...load.classes],
          teachingMinutes: load.teachingMinutes,
        });
      }
    });
  });

  console.log(`[extractAllStaffLoadsFromMultipleGrids] Combined loads for ${combinedLoads.size} staff members`);

  return combinedLoads;
};

/**
 * Validate that grid has required data for load letter generation
 */
export const validateGridForLoadLetters = (grid: StaffAllocationGrid | null): {
  valid: boolean;
  errors: string[];
} => {
  const errors: string[] = [];

  if (!grid) {
    errors.push('No grid selected');
    return { valid: false, errors };
  }

  if (!grid.classes || grid.classes.length === 0) {
    errors.push('Grid has no classes');
  }

  if (!grid.lines || grid.lines.length === 0) {
    errors.push('Grid has no lines defined');
  }

  if (!grid.semesters || grid.semesters.length === 0) {
    errors.push('Grid has no semesters defined');
  }

  // Check if at least one class has line assignments
  const hasAssignments = grid.classes?.some(
    (classItem) => classItem.lineAssignments && Object.keys(classItem.lineAssignments).length > 0
  );

  if (!hasAssignments) {
    errors.push('No classes have teacher assignments');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Get grid statistics for display
 */
export const getGridStats = (grid: StaffAllocationGrid): {
  totalClasses: number;
  totalStaff: number;
  unassignedClasses: number;
  linesCovered: number;
  totalLines: number;
} => {
  if (!grid) {
    return {
      totalClasses: 0,
      totalStaff: 0,
      unassignedClasses: 0,
      linesCovered: 0,
      totalLines: 0,
    };
  }

  const totalClasses = grid.classes?.length || 0;
  const staffCodes = getStaffCodesFromGrid(grid);
  const totalStaff = staffCodes.length;

  let unassignedClasses = 0;
  let linesCoveredSet = new Set<string>();

  grid.classes?.forEach((classItem) => {
    const hasAssignment = classItem.lineAssignments &&
      Object.values(classItem.lineAssignments).some(code => code);

    if (!hasAssignment) {
      unassignedClasses++;
    } else {
      Object.entries(classItem.lineAssignments).forEach(([lineId, code]) => {
        if (code) {
          linesCoveredSet.add(lineId);
        }
      });
    }
  });

  return {
    totalClasses,
    totalStaff,
    unassignedClasses,
    linesCovered: linesCoveredSet.size,
    totalLines: grid.lines?.length || 0,
  };
};
